import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import { Button, Modal, ModalSize, PasswordInput } from '@column/column-ui-kit';

import { isWebAuthnSupported } from '~/lib/webauthn';
import { StepUpChallenge } from '~/repositories/StepUpRepository';
import { Hint, Inner } from '~/styles';

import { CodeInput } from './CodeInput';
import { NotificationList } from './NotificationList';

const StyledHint = styled(Hint)`
  text-align: center;
  margin-top: 16px;
`;

const Resend = styled.span`
  display: inline-block;
  cursor: pointer;
  color: ${({ theme }) => theme.primary.background};
`;

const Controls = styled.div`
  display: grid;
  gap: 16px;
  margin-top: 16px;
`;

const Content = styled.div`
  max-width: 325px;
  padding-bottom: var(--page-margin, 24px);
`;

// passkey carries no proof of its own: the assertion is taken by the store, which owns the challenge options.
export type StepUpDialogProof = { code?: string; password?: string; passkey?: boolean };

export interface StepUpDialogProps {
  isOpen: boolean;
  challenge: StepUpChallenge | null;
  isLoading?: boolean;
  onSubmit: (proof: StepUpDialogProof) => void;
  onResend?: () => void;
  onClose: () => void;
}

export const StepUpDialog = ({ isOpen, challenge, isLoading, onSubmit, onResend, onClose }: StepUpDialogProps) => {
  const [code, setCode] = useState<string>('');
  const [password, setPassword] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      setCode('');
      setPassword('');
    }
  }, [isOpen, challenge]);

  const handleCodeSubmit = useCallback(() => {
    if (!isLoading && code.length === 6) {
      onSubmit({ code });
      setCode('');
    }
  }, [code, isLoading, onSubmit]);

  useEffect(() => {
    if (code.length === 6) {
      handleCodeSubmit();
    }
  }, [code, handleCodeSubmit]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={ModalSize.FitContent}>
      <Content>
        <Modal.IconCircle icon={challenge === StepUpChallenge.MfaSms ? 'Phone' : 'Lock'} type="primary" />

        <Modal.Content align="center">
          <Modal.Headline fullWidth>Authenticate</Modal.Headline>
          <Modal.Paragraph fullWidth>
            {challenge === StepUpChallenge.MfaSms && 'Please enter the 6-digit code we have sent to your phone.'}
            {challenge === StepUpChallenge.MfaTotp && 'Please enter the 6-digit code from your authenticator app.'}
            {challenge === StepUpChallenge.Password && 'Please enter your password to continue.'}
            {challenge === StepUpChallenge.Webauthn && 'Use your passkey to continue.'}
          </Modal.Paragraph>
          <NotificationList display="popup" />
          {challenge === StepUpChallenge.Webauthn ? (
            <Controls>
              {isWebAuthnSupported() ? (
                <Button type="button" fullWidth isLoading={isLoading} onClick={() => onSubmit({ passkey: true })}>
                  Continue with passkey
                </Button>
              ) : (
                <StyledHint>This browser does not support passkeys. Use a browser that does to continue.</StyledHint>
              )}
            </Controls>
          ) : challenge === StepUpChallenge.Password ? (
            <Controls>
              <PasswordInput
                autoFocus
                autoComplete="current-password"
                placeholder="Password"
                value={password}
                onChange={(value: string) => setPassword(value)}
              />
              <Button type="button" fullWidth isLoading={isLoading} onClick={() => password && onSubmit({ password })}>
                Continue
              </Button>
            </Controls>
          ) : (
            <>
              <CodeInput
                autoFocus
                placeholder="######"
                type="number"
                autoComplete="one-time-code"
                name="one-time-code"
                value={code}
                onChange={(value: string) => setCode(value)}
                isLoading={isLoading}
              />
              {challenge === StepUpChallenge.MfaSms && onResend && (
                <Inner p={0}>
                  <StyledHint>
                    Haven't received the code?{' '}
                    <Resend
                      onClick={(event: React.MouseEvent) => {
                        event.preventDefault();
                        onResend();
                      }}
                    >
                      Resend
                    </Resend>
                  </StyledHint>
                </Inner>
              )}
            </>
          )}
        </Modal.Content>
      </Content>
    </Modal>
  );
};
